import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { BRAND, GRADIENT, LINE, EASE, FONT, GLASS, SHADOW, GRID_LINES } from "./brand";

// ScalaVisione arayüz ilkelleri — ana sitedeki components/ui/Primitives.tsx ve
// fx/SpotlightCard.tsx dosyalarının MUI karşılıkları.
//
// Panel MUI ile, site Tailwind ile yazılmış durumda. Paneli Tailwind'e taşımak
// yerine sitenin tasarım GRAMERİNİ buraya taşıdık: aynı kicker, aynı gradyan
// başlık, aynı cam yüzey, aynı spotlight. Böylece iki ürün aynı dili konuşuyor
// ama panelin çalışan kodu yerinde kalıyor.

/* ─────────────────────────────── sx tarifleri ────────────────────────────── */

export const glassCard = {
  ...GLASS,
  borderRadius: "16px",
  boxShadow: SHADOW.card,
};

export const kickerSx = {
  fontFamily: FONT.mono,
  fontSize: "0.6875rem",
  fontWeight: 500,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: BRAND.inkFaint,
  fontVariantNumeric: "tabular-nums",
  display: "flex",
  alignItems: "center",
  gap: 1.5,
};

export const monoSx = {
  fontFamily: FONT.mono,
  fontVariantNumeric: "tabular-nums",
};

// Sitedeki text-h3 ölçeği, panel için bir tık küçültülmüş hâli.
export const headingSx = {
  fontWeight: 600,
  letterSpacing: "-0.038em",
  lineHeight: 1.08,
};

/* ───────────────────────────────── Bileşenler ────────────────────────────── */

/** Bölüm üstü mono etiket: ince gradyan çizgi + metin (veya numara + çizgi). */
export function Kicker({ children, index, sx, component = "p" }) {
  return (
    <Typography component={component} sx={{ ...kickerSx, ...sx }}>
      {index ? (
        <>
          <Box component="span" sx={{ color: BRAND.cyan }}>
            {index}
          </Box>
          <Box component="span" aria-hidden sx={{ height: "1px", width: 32, bgcolor: "rgba(234,240,248,0.2)" }} />
        </>
      ) : (
        <Box component="span" aria-hidden sx={{ height: "1px", width: 32, background: GRADIENT.kickerRule }} />
      )}
      {children}
    </Typography>
  );
}

/** Başlıkta gradyan uygulanan satır. Sitede olduğu gibi TEK satıra uygulanır. */
export function GradientText({ children, sx, component = "span" }) {
  return (
    <Box
      component={component}
      sx={{
        background: GRADIENT.text,
        backgroundClip: "text",
        WebkitBackgroundClip: "text",
        color: "transparent",
        paddingBlockEnd: "0.08em", // descender kırpılmasın
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Mono, pill biçimli küçük etiket (sitedeki Tag). */
export function Tag({ children, accent = false, tone, sx }) {
  const color = tone || (accent ? BRAND.cyan : BRAND.inkDim);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: 999,
        border: "1px solid",
        borderColor: accent || tone ? `${color}59` : "rgba(234,240,248,0.12)",
        bgcolor: accent || tone ? `${color}1a` : "rgba(234,240,248,0.04)",
        color,
        px: 1.25,
        py: 0.35,
        fontFamily: FONT.mono,
        fontSize: "0.6875rem",
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** İnce, iki ucu sönümlenen ayırıcı. */
export function Rule({ sx }) {
  return <Box aria-hidden sx={{ height: "1px", width: "100%", background: GRADIENT.rule, ...sx }} />;
}

/** Dekoratif ışık lekesi (sitedeki glow-cyan / glow-ember). */
export function Glow({ tone = "cyan", size = 320, sx }) {
  const color = tone === "ember" ? BRAND.ember : BRAND.cyan;
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        pointerEvents: "none",
        width: size,
        height: size,
        borderRadius: "50%",
        background: `radial-gradient(closest-side, ${color}${tone === "ember" ? "4d" : "57"}, transparent)`,
        filter: `blur(${tone === "ember" ? 52 : 46}px)`,
        ...sx,
      }}
    />
  );
}

/**
 * İmleci takip eden ışık. Efektin kendisi CSS'te (index.css .spotlight);
 * burada sadece --mx/--my yazılıyor, böylece her karede React render olmuyor.
 */
export function SpotlightCard({ children, sx, className = "", ...rest }) {
  const ref = useRef(null);

  return (
    <Box
      ref={ref}
      className={`spotlight ${className}`}
      onPointerMove={(event) => {
        if (event.pointerType !== "mouse") return;
        const node = ref.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        node.style.setProperty("--mx", `${event.clientX - rect.left}px`);
        node.style.setProperty("--my", `${event.clientY - rect.top}px`);
      }}
      sx={sx}
      {...rest}
    >
      {children}
    </Box>
  );
}

/**
 * Sayfa başlığı bloğu — sitedeki SectionHeader'ın panel karşılığı:
 * mono etiket + sıkı harf aralıklı başlık (bir kelimesi gradyanlı) + sağda eylem.
 */
export function PageHeader({ kicker, index, title, accent, lead, action, sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: { xs: "flex-start", md: "flex-end" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        mb: 4,
        ...sx,
      }}
    >
      <Box sx={{ maxWidth: 680 }}>
        <Kicker index={index}>{kicker}</Kicker>
        <Typography
          variant="h4"
          sx={{ ...headingSx, mt: 1.5, fontSize: { xs: "1.9rem", md: "2.4rem" }, letterSpacing: "-0.046em" }}
        >
          {title}
          {accent ? (
            <>
              {" "}
              <GradientText>{accent}</GradientText>
            </>
          ) : null}
        </Typography>
        {lead ? (
          <Typography sx={{ mt: 1.5, color: BRAND.inkDim, fontSize: "0.9375rem", lineHeight: 1.65 }}>
            {lead}
          </Typography>
        ) : null}
      </Box>
      {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
    </Box>
  );
}

/** Cam yüzeyli kart + isteğe bağlı spotlight. */
export function GlassPanel({ children, spotlight = false, sx, ...rest }) {
  const content = (
    <Box sx={{ ...glassCard, p: 2.5, ...sx }} {...rest}>
      {children}
    </Box>
  );
  return spotlight ? (
    <SpotlightCard sx={{ borderRadius: "16px", height: "100%" }}>{content}</SpotlightCard>
  ) : (
    content
  );
}

export { BRAND, GRADIENT, LINE, EASE, FONT, GLASS, SHADOW, GRID_LINES };
